import React from 'react';
import { useTranslation } from 'react-i18next';
import { TruckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

/**
 * Visual category for a Logisti-integration field requirement.
 *
 * - `required`    — hard requirement when the field is needed by
 *                   the Logisti integration. Renders in red to signal
 *                   a blocker.
 * - `recommended` — soft requirement. The driver/vehicle record can
 *                   still be created, but Logisti integration may
 *                   not pick it up without the field. Renders in
 *                   emerald.
 *
 * Mirrors {@link FinancingFieldBadge} so the two integration badges
 * read as part of the same UI family.
 */
export type LogistiFieldBadgeVariant = 'required' | 'recommended';

export interface LogistiFieldBadgeProps {
  /** Required (hard, red) vs recommended (soft, emerald). */
  variant: LogistiFieldBadgeVariant;
  /**
   * Whether to draw the small truck icon. Defaults to `true` because
   * the icon is what visually anchors "this is about Logisti" —
   * without it the chip just looks like a generic Required/Optional
   * marker.
   */
  showIcon?: boolean;
  /** Override the rendered label (defaults to the i18n translation). */
  label?: string;
  className?: string;
}

/**
 * `LogistiFieldBadge` — small inline chip rendered next to a
 * field label to communicate that the field is needed (or
 * recommended) for the **Logisti** integration specifically.
 *
 * Driver and vehicle creation forms only require name+phone (driver)
 * and type+plate (vehicle). Every other field (driver ID, license
 * image, vehicle registration image, brand, year, color, etc.) is
 * not required to *create* the record but is needed for the Logisti
 * integration to verify and dispatch the entity.
 *
 * The badge is purely a static marker (no "provided" / satisfied
 * state). It documents the field's role; whether the field is
 * actually filled is communicated by the input itself.
 */
export function LogistiFieldBadge({
  variant,
  showIcon = true,
  label,
  className,
}: LogistiFieldBadgeProps) {
  const { t } = useTranslation('c-main');

  const resolvedLabel = label ?? t(LABEL_KEYS[variant]);
  const styles = STYLES[variant];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        styles,
        className,
      )}
      title={resolvedLabel}>
      {showIcon ? <TruckIcon className="size-3" /> : null}
      {resolvedLabel}
    </span>
  );
}

const STYLES: Record<LogistiFieldBadgeVariant, string> = {
  required: 'bg-red-50 text-red-700',
  recommended: 'bg-emerald-50 text-emerald-700',
};

const LABEL_KEYS: Record<LogistiFieldBadgeVariant, string> = {
  required: 'logisti_field.required_for_logisti',
  recommended: 'logisti_field.recommended_for_logisti',
};
